import { z } from 'zod';
import { cuid } from './primitives.js';
import { competencia } from './centros-custo.js';
import type { CopilotoAcaoCartao, CopilotoAcaoCartaoComToken } from './copiloto-acoes.js';

/**
 * Copiloto (IA), somente leitura. O front manda SO a conversa e a tela (rota + entidade aberta); nunca numero: a API monta o contexto
 * com as mesmas permissoes das rotas (ferramentas de leitura executadas como o usuario).
 */
export const COPILOTO_ENTIDADES = ['item', 'tabela', 'orcamento', 'rateio', 'centro', 'lote'] as const;

const contextoCopiloto = z.object({
  /** Rota da tela (ex.: /precificar/formacao). */
  rota: z.string().regex(/^\/[A-Za-z0-9/_\-$.]{0,198}$/, 'Rota invalida'),
  entidade: z
    .object({
      tipo: z.enum(COPILOTO_ENTIDADES),
      id: cuid.optional(),
      competencia: competencia.optional(),
    })
    .optional(),
  /** Filtros/selecao da tela (so identificadores e termos de busca, ex.: itemId, tabelaId, condicaoId, q); a API valida cada chave. */
  filtros: z.record(z.string().regex(/^[A-Za-z]{1,30}$/), z.string().max(100)).refine((f) => Object.keys(f).length <= 10, 'Filtros demais').optional(),
});

/** Limites dos anexos (Sessao 13). */
export const COPILOTO_ANEXO_MAX_BYTES = 10 * 1024 * 1024;
export const COPILOTO_ANEXOS_POR_MENSAGEM = 5;

/**
 * Uma NOVA mensagem do usuario. O historico vem do banco (conversa do proprio usuario); sem `conversaId` abre uma conversa nova.
 * Anexos sao enviados antes (POST /copiloto/anexos) e referenciados por id.
 */
export const copilotoMensagemSchema = z.object({
  conversaId: cuid.optional(),
  texto: z.string().trim().min(1).max(8000),
  anexoIds: z.array(cuid).max(COPILOTO_ANEXOS_POR_MENSAGEM).default([]),
  contexto: contextoCopiloto,
});
export type CopilotoMensagemDto = z.infer<typeof copilotoMensagemSchema>;

export const renomearConversaSchema = z.object({ titulo: z.string().trim().min(1).max(120) });
export type RenomearConversaDto = z.infer<typeof renomearConversaSchema>;
export const listarConversasSchema = z.object({ q: z.string().trim().max(100).optional() });
export type ListarConversasQuery = z.infer<typeof listarConversasSchema>;

export type CopilotoTipoAnexo = 'imagem' | 'pdf' | 'planilha';
export interface CopilotoAnexoResponse {
  id: string;
  nome: string;
  mime: string;
  tamanho: number;
  tipo: CopilotoTipoAnexo;
}
export interface CopilotoConversaResumo {
  id: string;
  titulo: string;
  tela: string;
  criadaEm: string;
  atualizadaEm: string;
}
export interface CopilotoMensagemResponse {
  id: string;
  papel: 'user' | 'assistant';
  conteudo: string;
  anexos: CopilotoAnexoResponse[];
  /** Cartoes de acao mostrados nesta mensagem (status atual, sem token). */
  acoes?: CopilotoAcaoCartao[];
  criadaEm: string;
}
export interface CopilotoConversaResponse extends CopilotoConversaResumo {
  mensagens: CopilotoMensagemResponse[];
}
export interface CopilotoTranscricaoResponse {
  texto: string;
}

/** GET /copiloto/tela?rota=: nome da tela e perguntas sugeridas (do registro de manuais). */
export interface CopilotoTelaResponse {
  tela: string | null;
  sugestoes: string[];
}

export interface CopilotoStatusResponse {
  configurado: boolean;
  modelo: string | null;
  /** Ha servico de transcricao de audio configurado (o botao de microfone so aparece com ele). */
  transcricao: boolean;
  /** A env COPILOTO_ACOES=on (teto): so entao a opcao aparece em Empresa > Parametros. */
  acoesDisponiveis: boolean;
  /** Acoes ligadas NESTA empresa e permitidas pela env (o que o Copiloto oferece). */
  acoes: boolean;
}

/** Eventos do SSE de POST /copiloto/mensagens (`event: <tipo>`, `data: <json>`). */
export type CopilotoEvento =
  | { tipo: 'conversa'; id: string; titulo: string }
  | { tipo: 'titulo'; id: string; titulo: string }
  | { tipo: 'texto'; delta: string }
  | { tipo: 'ferramenta'; nome: string }
  /** Cartao Confirmar/Cancelar de uma acao proposta (so a API grava; o clique executa). */
  | { tipo: 'acao'; cartao: CopilotoAcaoCartaoComToken }
  /** `abrir_tela`: o front navega (rota da lista fechada). */
  | { tipo: 'navegar'; rota: string; filtros?: Record<string, string> }
  | { tipo: 'fim'; tokensEntrada: number; tokensSaida: number }
  | { tipo: 'erro'; code: string; message: string };
